import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { InvestmentCompany } from '../types';
import { Favicon } from '../utils/favicon';
import { formatEuro, formatMultiple } from '../utils/formatters';
import {
  computeAllFutureReturnProjections,
  computeFutureReturnsTotals,
  aggregateTimelineByYear,
  type AggregatedTimelinePoint,
  type FutureReturnProjection,
} from '../utils/futureReturns';
import { GlobalButton } from './GlobalButton';
import styles from './FutureReturnsSection.module.css';

type Props = {
  companies: InvestmentCompany[];
  onUpdateCompanies?: (updater: (companies: InvestmentCompany[]) => InvestmentCompany[]) => void;
};

const CHART_WIDTH = 720;
const CHART_HEIGHT = 280;
const MARGIN = { top: 24, right: 90, bottom: 40, left: 12 };

function FutureReturnsChart({
  timelineData,
  totals,
}: {
  timelineData: AggregatedTimelinePoint[];
  totals: ReturnType<typeof computeFutureReturnsTotals>;
}) {
  const geometry = useMemo(() => {
    if (timelineData.length === 0) return null;

    const innerWidth = CHART_WIDTH - MARGIN.left - MARGIN.right;
    const innerHeight = CHART_HEIGHT - MARGIN.top - MARGIN.bottom;

    const allValues = timelineData.flatMap((p) => [p.medium, p.good, p.amazing, p.custom || 0]);
    const maxValue = Math.max(...allValues, totals.amazing || 1);
    const padding = maxValue * 0.1;
    const yMax = maxValue + padding;

    const years = timelineData.map((p) => p.year);
    const minYear = Math.min(...years);
    const maxYear = Math.max(...years);
    const yearSpan = maxYear - minYear || 1;

    const xScale = (year: number) =>
      MARGIN.left + ((year - minYear) / yearSpan) * innerWidth;
    const yScale = (value: number) => MARGIN.top + ((yMax - value) / yMax) * innerHeight;

    const buildPath = (accessor: (p: AggregatedTimelinePoint) => number): string => {
      let cumulative = 0;
      return timelineData
        .map((point, index) => {
          cumulative += accessor(point);
          const x = xScale(point.year);
          const y = yScale(cumulative);
          return `${index === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${y.toFixed(1)}`;
        })
        .join(' ');
    };

    return {
      innerWidth,
      innerHeight,
      minYear,
      maxYear,
      yMax,
      xScale,
      yScale,
      mediumPath: buildPath((p) => p.medium),
      goodPath: buildPath((p) => p.good),
      amazingPath: buildPath((p) => p.amazing),
      customPath: totals.custom > 0 ? buildPath((p) => p.custom) : null,
    };
  }, [timelineData, totals]);

  if (!geometry) {
    return (
      <p className={styles.chartEmpty}>
        Add expected lead times to your investments to see the timeline chart.
      </p>
    );
  }

  return (
    <figure className={styles.chart}>
      <svg
        viewBox={`0 0 ${CHART_WIDTH} ${CHART_HEIGHT}`}
        className={styles.svg}
        preserveAspectRatio="xMidYMid meet"
        role="img"
        aria-label="Future returns projection by year"
      >
        <line
          x1={MARGIN.left}
          x2={CHART_WIDTH - MARGIN.right}
          y1={MARGIN.top + geometry.innerHeight}
          y2={MARGIN.top + geometry.innerHeight}
          className={styles.gridLine}
        />

        <path d={geometry.mediumPath} className={styles.mediumLine} />
        <path d={geometry.goodPath} className={styles.goodLine} />
        <path d={geometry.amazingPath} className={styles.amazingLine} />
        {geometry.customPath && (
          <path d={geometry.customPath} className={styles.customLine} />
        )}

        <text
          x={MARGIN.left}
          y={CHART_HEIGHT - 8}
          className={styles.axisLabel}
          textAnchor="start"
        >
          {geometry.minYear}
        </text>
        <text
          x={CHART_WIDTH - MARGIN.right}
          y={CHART_HEIGHT - 8}
          className={styles.axisLabel}
          textAnchor="end"
        >
          {geometry.maxYear}
        </text>
      </svg>

      <ul className={styles.legend} aria-label="Scenario legend">
        <li className={styles.legendItem}>
          <span className={`${styles.legendSwatch} ${styles.swatchMedium}`} aria-hidden />
          <span>Medium (2.5×) <span className={styles.legendValue}>{formatEuro(totals.medium)}</span></span>
        </li>
        <li className={styles.legendItem}>
          <span className={`${styles.legendSwatch} ${styles.swatchGood}`} aria-hidden />
          <span>Good (10×) <span className={styles.legendValue}>{formatEuro(totals.good)}</span></span>
        </li>
        <li className={styles.legendItem}>
          <span className={`${styles.legendSwatch} ${styles.swatchAmazing}`} aria-hidden />
          <span>Amazing (20×) <span className={styles.legendValue}>{formatEuro(totals.amazing)}</span></span>
        </li>
        {totals.custom > 0 && (
          <li className={styles.legendItem}>
            <span className={`${styles.legendSwatch} ${styles.swatchCustom}`} aria-hidden />
            <span>Your estimates <span className={styles.legendValue}>{formatEuro(totals.custom)}</span></span>
          </li>
        )}
      </ul>
    </figure>
  );
}

function FutureReturnsTable({
  projections,
  companies,
}: {
  projections: FutureReturnProjection[];
  companies: InvestmentCompany[];
}) {
  const navigate = useNavigate();

  const companyMap = useMemo(() => {
    const map = new Map<string, InvestmentCompany>();
    for (const c of companies) {
      map.set(c.id, c);
    }
    return map;
  }, [companies]);

  const sortedProjections = useMemo(() => {
    return [...projections].sort((a, b) => {
      if (a.expectedLeadTime != null && b.expectedLeadTime != null) {
        return a.expectedLeadTime - b.expectedLeadTime;
      }
      if (a.expectedLeadTime != null) return -1;
      if (b.expectedLeadTime != null) return 1;
      return b.ticketSize - a.ticketSize;
    });
  }, [projections]);

  return (
    <div className="table-card">
      <div className="table-scroll">
        <table className="data-table">
          <thead>
            <tr>
              <th>Company</th>
              <th>Ticket</th>
              <th>Lead time</th>
              <th>Est. return</th>
              <th>Medium (2.5×)</th>
              <th>Good (10×)</th>
              <th>Amazing (20×)</th>
            </tr>
          </thead>
          <tbody>
            {sortedProjections.map((proj) => {
              const company = companyMap.get(proj.companyId);
              return (
                <tr
                  key={proj.companyId}
                  className="click-row"
                  onClick={() => navigate(`/investments/${proj.companyId}`)}
                >
                  <td>
                    <div className="companyCell">
                      {company?.websiteUrl ? (
                        <Favicon
                          websiteUrl={company.websiteUrl}
                          size={20}
                          alt={`${proj.companyName} site`}
                          className="companyFavicon"
                        />
                      ) : (
                        <span
                          className="companyFavicon"
                          style={{
                            display: 'inline-block',
                            width: 20,
                            height: 20,
                            borderRadius: 6,
                            background: 'var(--global-separator-color)',
                          }}
                          aria-hidden
                        />
                      )}
                      <span>{proj.companyName}</span>
                    </div>
                  </td>
                  <td>{formatEuro(proj.ticketSize, true)}</td>
                  <td>
                    {proj.expectedLeadTime != null ? (
                      <span className={styles.leadTimeValue}>
                        {proj.expectedLeadTime} {proj.expectedLeadTime === 1 ? 'year' : 'years'}
                      </span>
                    ) : (
                      <span className="muted">—</span>
                    )}
                  </td>
                  <td>
                    {proj.expectedMultiple != null ? (
                      <span className={styles.customMultiple}>
                        {formatMultiple(proj.expectedMultiple)} → {formatEuro(proj.scenarios.custom!, true)}
                      </span>
                    ) : (
                      <span className="muted">—</span>
                    )}
                  </td>
                  <td className={styles.scenarioMedium}>{formatEuro(proj.scenarios.medium, true)}</td>
                  <td className={styles.scenarioGood}>{formatEuro(proj.scenarios.good, true)}</td>
                  <td className={styles.scenarioAmazing}>{formatEuro(proj.scenarios.amazing, true)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function FutureReturnsSection({ companies, onUpdateCompanies }: Props) {
  const [bulkUpdating, setBulkUpdating] = useState(false);
  const [bulkUpdateDone, setBulkUpdateDone] = useState(false);

  const projections = useMemo(() => computeAllFutureReturnProjections(companies), [companies]);
  const totals = useMemo(() => computeFutureReturnsTotals(projections), [projections]);
  const timelineData = useMemo(() => aggregateTimelineByYear(projections), [projections]);

  const activeCompaniesWithoutLeadTime = useMemo(() => {
    return companies.filter(
      (c) =>
        (c.investmentStatus === 'active' || !c.investmentStatus) &&
        (c.expectedReturnLeadTime == null || !Number.isFinite(c.expectedReturnLeadTime))
    );
  }, [companies]);

  const handleBulkSetLeadTime = (years: number) => {
    if (!onUpdateCompanies) return;
    setBulkUpdating(true);
    
    onUpdateCompanies((prev) =>
      prev.map((c) => {
        if (c.investmentStatus === 'active' || !c.investmentStatus) {
          return {
            ...c,
            expectedReturnLeadTime: years,
            updatedAt: new Date().toISOString(),
          };
        }
        return c;
      })
    );

    setTimeout(() => {
      setBulkUpdating(false);
      setBulkUpdateDone(true);
      setTimeout(() => setBulkUpdateDone(false), 3000);
    }, 500);
  };

  if (projections.length === 0) {
    return (
      <section className={styles.section} aria-label="Future returns">
        <div className={styles.emptyState}>
          <p className={styles.emptyStateText}>
            Add active investments to see potential future returns across different scenarios.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className={styles.section} aria-label="Future returns">
      <div className={styles.summaryGrid}>
        <div className={styles.summaryCard}>
          <span className={styles.summaryLabel}>Total invested</span>
          <span className={styles.summaryValue}>{formatEuro(totals.totalTickets)}</span>
        </div>
        <div className={styles.summaryCard}>
          <span className={styles.summaryLabel}>Medium outcome (2.5×)</span>
          <span className={`${styles.summaryValue} ${styles.summaryMedium}`}>
            {formatEuro(totals.medium)}
          </span>
        </div>
        <div className={styles.summaryCard}>
          <span className={styles.summaryLabel}>Good outcome (10×)</span>
          <span className={`${styles.summaryValue} ${styles.summaryGood}`}>
            {formatEuro(totals.good)}
          </span>
        </div>
        <div className={styles.summaryCard}>
          <span className={styles.summaryLabel}>Amazing outcome (20×)</span>
          <span className={`${styles.summaryValue} ${styles.summaryAmazing}`}>
            {formatEuro(totals.amazing)}
          </span>
        </div>
        {totals.custom > 0 && (
          <div className={styles.summaryCard}>
            <span className={styles.summaryLabel}>Your estimates total</span>
            <span className={`${styles.summaryValue} ${styles.summaryCustom}`}>
              {formatEuro(totals.custom)}
            </span>
          </div>
        )}
        <div className={styles.summaryCard}>
          <span className={styles.summaryLabel}>With timeline set</span>
          <span className={styles.summaryValue}>
            {totals.projectionsWithTimeline} / {projections.length}
          </span>
        </div>
      </div>

      {onUpdateCompanies && activeCompaniesWithoutLeadTime.length > 0 && (
        <div className={styles.bulkUpdatePanel}>
          <p className={styles.bulkUpdateText}>
            {activeCompaniesWithoutLeadTime.length} investment{activeCompaniesWithoutLeadTime.length === 1 ? '' : 's'} missing lead time.
            Set a default for all active investments:
          </p>
          <div className={styles.bulkUpdateButtons}>
            <GlobalButton
              variant="secondary"
              onClick={() => handleBulkSetLeadTime(3)}
              disabled={bulkUpdating}
            >
              3 years
            </GlobalButton>
            <GlobalButton
              variant="secondary"
              onClick={() => handleBulkSetLeadTime(4)}
              disabled={bulkUpdating}
            >
              4 years
            </GlobalButton>
            <GlobalButton
              variant="secondary"
              onClick={() => handleBulkSetLeadTime(5)}
              disabled={bulkUpdating}
            >
              5 years
            </GlobalButton>
            <GlobalButton
              variant="secondary"
              onClick={() => handleBulkSetLeadTime(7)}
              disabled={bulkUpdating}
            >
              7 years
            </GlobalButton>
          </div>
          {bulkUpdateDone && (
            <p className={styles.bulkUpdateSuccess}>✓ Updated all active investments!</p>
          )}
        </div>
      )}

      <div className={styles.chartSection}>
        <h3 className={styles.chartTitle}>Cumulative returns timeline</h3>
        <p className={styles.chartSubtitle}>
          Expected returns by year based on your lead time estimates.
        </p>
        <FutureReturnsChart timelineData={timelineData} totals={totals} />
      </div>

      <div className={styles.tableSection}>
        <h3 className={styles.tableTitle}>Projections by investment</h3>
        <p className={styles.tableSubtitle}>
          Click on a row to edit expected lead time and multiple for more accurate projections.
        </p>
        <FutureReturnsTable projections={projections} companies={companies} />
      </div>
    </section>
  );
}
